import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, LayoutDashboard, Store, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const NAV = [
  { to: '/', label: 'Beranda' },
  { to: '/produk', label: 'Produk' },
  { to: '/kategori', label: 'Kategori' },
  { to: '/artikel', label: 'Artikel' },
];

export default function Header() {
  const { user, logout, dashboardPath } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-border/60" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.webp" alt="TokoSipil.id" className="h-8 sm:h-9 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className="text-sm font-medium text-zinc-600 hover:text-brand-black transition-colors hover:underline underline-offset-8 decoration-brand-red decoration-2">
                {n.label}
              </Link>
            ))}
            <Link to="/jadi-seller" data-testid="nav-jadi-seller"
              className="text-sm font-medium text-brand-red hover:text-brand-redhover transition-colors hover:underline underline-offset-8">
              Jadi Seller
            </Link>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={() => navigate('/produk')} className="p-2 text-zinc-600 hover:text-brand-black transition-colors" data-testid="header-search-btn" aria-label="Cari">
              <Search className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <Link to="/keranjang" className="relative p-2 text-zinc-600 hover:text-brand-black transition-colors" data-testid="cart-icon" aria-label="Keranjang">
              <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-brand-red text-white text-[10px] font-semibold w-4.5 h-4.5 min-w-[18px] min-h-[18px] flex items-center justify-center rounded-full" data-testid="cart-count">
                  {count}
                </span>
              )}
            </Link>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 border border-border px-3 py-1.5 hover:border-brand-black transition-colors" data-testid="user-menu-trigger">
                    <div className="w-6 h-6 bg-brand-black text-white flex items-center justify-center text-xs font-semibold rounded-sm overflow-hidden">
                      {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : user.name?.[0]?.toUpperCase()}
                    </div>
                    <span className="text-sm font-medium hidden sm:block max-w-[100px] truncate">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onClick={() => navigate(dashboardPath())} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" strokeWidth={1.5} /> Dashboard
                  </DropdownMenuItem>
                  {user.role === 'buyer' && (
                    <DropdownMenuItem onClick={() => navigate('/jadi-seller')} data-testid="menu-jadi-seller">
                      <Store className="w-4 h-4 mr-2" strokeWidth={1.5} /> Jadi Seller
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate('/'); }} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> Keluar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/masuk" data-testid="header-login-btn"
                  className="text-sm font-medium px-4 py-2 border border-brand-black hover:bg-brand-black hover:text-white transition-colors">
                  Masuk
                </Link>
                <Link to="/daftar" data-testid="header-register-btn"
                  className="text-sm font-medium px-4 py-2 bg-brand-red text-white hover:bg-brand-redhover transition-colors">
                  Daftar
                </Link>
              </div>
            )}

            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-white animate-fade-in" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                className="py-2.5 px-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors">
                {n.label}
              </Link>
            ))}
            <Link to="/jadi-seller" onClick={() => setOpen(false)} className="py-2.5 px-2 text-sm font-medium text-brand-red">
              Jadi Seller
            </Link>
            {!user && (
              <div className="flex gap-2 pt-3 border-t border-border mt-2">
                <Link to="/masuk" onClick={() => setOpen(false)} className="flex-1 text-center text-sm font-medium px-4 py-2.5 border border-brand-black">Masuk</Link>
                <Link to="/daftar" onClick={() => setOpen(false)} className="flex-1 text-center text-sm font-medium px-4 py-2.5 bg-brand-red text-white">Daftar</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
